import BasePopupHandler from './BasePopupHandler';
import BaseUISystem from '../BaseUISystem';
import Game from '../../../Game';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import Armature from '../../../Prototype/Armature';
import OverlayComponent from '../../../Component/UI/Overlay';
import Sprite from '../../../Prototype/Sprite';
import Point from '../../../Utility/Point';
import { Anim, StartAnimationClip } from '../../Animation/AnimationHelper';
import { ContainerFadeIn } from '../../Animation/Clip';
import UIItem from '../../../Prototype/UI/UIItem';
import { TextAlignment } from '../../../Prototype/UI/TextAlignment';
import UITextComponent from '../../../Component/UI/Text';
import { isWebPSupported } from '../../../Utility/WebPFunctions';
import Renderable from '../../../Component/Renderable';
import SpriteComponent from '../../../Component/Render/Sprite';
import { PopupID } from '../../../State/UI/PopupID';

export default class ThankYouFirstPurchaseHandler extends BasePopupHandler {
    private armature: Armature;
    private thankYouObj: GameObject;
    private textObj: GameObject;
    private coinTextObj: GameObject;

    private onClickHookCache: Function;

    private isClosing: boolean = false;
    private canClose: boolean = false;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.THANK_YOU_FIRST_PURCHASE, uiSystem);
    }

    public async preload() {
        this.resources.set(
            'thankyoufirstpurchase_text',
            `resource/graphics/other/thankyoulocalization/purchase_popup_text_${
                Game.locale.getLanguage()
            }.png${isWebPSupported() ? ".webp" : ""}`
        );
        super.preload();
    }

    private closeIfPossible(): void {
        if(!this.isClosing && this.canClose) {
            this.system.overlayPopup.off(
                GameObjectEvent.POINTER_DOWN,
                // @ts-ignore
                this.onClickHookCache
            );
            this.isClosing = true;

            Anim(this.thankYouObj, { alpha: 0, duration: 200 })
                .then(() => {
                    // We can destroy things because this UI is only shown once.

                    this.thankYouObj.parent.removeChild(this.thankYouObj);
                    this.thankYouObj.destroy();
                    this.thankYouObj = null;
                });

            this.armature.container.animation.gotoAndPlayByProgress("hide", 0, 1);
            const func: () => void = () => {
                this.armature.container.visible = false;
                setTimeout(() => {
                    this.armature.container.removeDBEventListener("complete", func, this);
                    this.closePopup();
                    this.system.overlayPopup.removeChild(this.armature);
                    this.armature.destroy();
                    this.armature = null;
                }, 200);
            };
            this.armature.container.addDBEventListener("complete", func, this);
        }
    }

    private createTextObject(): GameObject {
        const textKey: string = "Thanks_For_First_Purchase";
        let textObj: GameObject = UIItem.createText({
            name: "someuitext",
            textLocalizationKey: textKey,
            style: Game.style.getStyle('popup.thankyou_firstpurchase.thankyoutext'),
            align: TextAlignment.CENTER,
        });
        textObj.container.x = -(textObj.getComponent(UITextComponent).container.width / 2);
        textObj.container.y += 85;
        textObj.container.pivot.set(0.5, 0.5);
        return textObj;
    }

    private createCoinDisplay(): GameObject {
        let coinTextObj: GameObject = UIItem.createText({
            name: "someuitext",
            text: "+" + (2500).toLocaleString(),
            style: Game.style.getStyle('popup.thankyou_firstpurchase.cointext'),
            align: TextAlignment.CENTER,
        });
        coinTextObj.container.x = -(coinTextObj.getComponent(UITextComponent).container.width / 3);
        coinTextObj.container.y = 165;
        coinTextObj.container.pivot.set(0.5, 0.5);

        let coinObj: GameObject = GameObject.Instantiate(Sprite, {
            position: {x: 0, y: 0},
            texture: "coin_large",
            anchor: new Point(0.5, 0.5),
            alpha: 1
        });
        coinObj.container.x -= coinObj.container.width / 2;
        coinObj.container.y += coinObj.container.height / 1.6;
        coinTextObj.container.addChild(coinObj.container);

        return coinTextObj;
    }

    protected createPopup(params?: any): void {
        // add on-click hooks
        this.onClickHookCache = this.closeIfPossible.bind(this);
        this.system.overlayPopup.on(
            GameObjectEvent.POINTER_DOWN,
            // @ts-ignore
            this.onClickHookCache
        );

        // Create DB animation
        this.armature = GameObject.Instantiate(Armature, {
            dragonbonesName: "firework2_exp2",
            armatureName: 'armatureName',
            x: this.system.overlayPopup.getComponent(OverlayComponent).container.width / 2,
            y: this.system.overlayPopup.getComponent(OverlayComponent).container.height / 2,
        });
        this.armature.container.visible = false;
        this.element.addChild(this.armature);

        // Create localized "Thanks!" text image
        this.thankYouObj = GameObject.Instantiate(Sprite, {
            position: {x: 0, y: -10},
            texture: "thankyoufirstpurchase_text",
            anchor: new Point(0.5, 0.5),
            alpha: 0
        });
        this.armature.addChild(this.thankYouObj);

        // Create "Thanks for first purchase" text
        this.textObj = this.createTextObject();
        this.thankYouObj.container.addChild(this.textObj.container);

        // Create ":coin: +2500" display
        this.coinTextObj = this.createCoinDisplay();
        this.thankYouObj.container.addChild(this.coinTextObj.container);

        // Start animation
        setTimeout(() => {
            this.armature.getComponent(Renderable).visible = this.thankYouObj.getComponent(SpriteComponent).visible = true;
            this.armature.container.animation.gotoAndPlayByProgress("show");

            StartAnimationClip(
                new ContainerFadeIn(this.thankYouObj, {
                    initialDelay: 375,
                })
            );

            const dbAnimCallback: () => void = () => {
                this.armature.container.removeDBEventListener("complete", dbAnimCallback, this);
                setTimeout(() => {
                    this.armature.container.animation.gotoAndPlayByProgress("idle", 0, 0);
                    setTimeout(() => {
                        this.canClose = true;
                    }, 400);
                }, 0);
            };
            this.armature.container.addDBEventListener("complete", dbAnimCallback, this);
        }, 500);
    }

    public onShow(params: {amount: number}) {
        super.onShow(params);
        const _component: UITextComponent = this.coinTextObj.getComponent(UITextComponent);
        _component.text = `+${(params?.amount || 0).toLocaleString()}`;
        _component.container.x = -(_component.container.width / 3);
    }
}
